import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { ApplicantType, Page, PvApplication, PvApplicationState } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, FileHashField, Modal, PageHeader, RoleNotice, Spinner, StatusBadge, type FileHashValue } from '../../ui/components';

const OFFICER_ROLES = ['ENERGY_OFFICER'];
const STATES: PvApplicationState[] = ['SUBMITTED', 'INSTALLER_COSIGNED', 'UNDER_REVIEW', 'APPROVED', 'REJECTED', 'PUC_HANDED_OFF', 'COMMISSIONED', 'WITHDRAWN'];

/** PV solar approvals: submit -> installer co-sign (M29 registered-professional check) -> claim/review -> approve -> PUC net-metering handoff -> commission (M35). */
export function PvApplications() {
  const { persona } = useAuth();
  const isOfficer = persona?.roles.includes('ENERGY_OFFICER') ?? false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m35/pv-applications?${params.toString()}`;
  }, [status, appliedQ]);
  const listQ = useApiQuery<Page<PvApplication>>(path);

  return (
    <div>
      <PageHeader
        title="PV Solar Applications"
        subtitle="Submit -> installer co-signs with M29 verification -> officer review -> approve -> PUC net-metering handoff -> commission (M35)."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New PV application
          </button>
        }
      />

      <form
        className="mb-4 flex items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setAppliedQ(q);
        }}
      >
        <input className="field-input !w-56" placeholder="Ref, address, installer…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All states</option>
          {STATES.map((s) => (
            <option key={s} value={s}>
              {s.replaceAll('_', ' ')}
            </option>
          ))}
        </select>
        <button type="submit" className="btn-primary">
          Search
        </button>
      </form>

      {!isOfficer ? <RoleNotice roles={OFFICER_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No PV applications match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref</th>
                <th className="table-head">Premises</th>
                <th className="table-head">System kW</th>
                <th className="table-head">Installer</th>
                <th className="table-head">State</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((a) => (
                <tr key={a.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(a.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{a.refNo}</td>
                  <td className="table-cell text-xs text-slate-500">{a.premisesAddress}</td>
                  <td className="table-cell tabular-nums">{a.systemKw}</td>
                  <td className="table-cell text-xs text-slate-500">{a.installerName}</td>
                  <td className="table-cell">
                    <StatusBadge status={a.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <DetailModal id={selectedId} isOfficer={isOfficer} onClose={() => setSelectedId(null)} onChanged={() => listQ.reload()} />
      ) : null}
      {showCreate ? (
        <CreateModal
          isOfficer={isOfficer}
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function DetailModal({ id, isOfficer, onClose, onChanged }: { id: string; isOfficer: boolean; onClose: () => void; onChanged: () => void }) {
  const q = useApiQuery<PvApplication>(`/api/v1/m35/pv-applications/${id}`);
  const a = q.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [singleLine, setSingleLine] = useState<FileHashValue | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [pucRef, setPucRef] = useState('');
  const [meterSwapDate, setMeterSwapDate] = useState('');
  const [withdrawReason, setWithdrawReason] = useState('');

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      q.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={a ? a.refNo : 'PV application'} onClose={onClose} wide>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {a ? (
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
            <Detail label="State" value={<StatusBadge status={a.state} />} />
            <Detail label="System size" value={`${a.systemKw} kW`} />
            <Detail label="District" value={a.district} />
            <Detail label="Premises" value={a.premisesAddress} />
            <Detail label="Installer" value={`${a.installerName} (${a.installerLicenceNo})`} />
            <Detail label="Installer M29-verified" value={a.installerVerified ? <StatusBadge status="VERIFIED" /> : <StatusBadge status="PENDING" />} />
            {a.approvalCertSerial ? <Detail label="Approval certificate" value={a.approvalCertSerial} mono /> : null}
            {a.pucApplicationRef ? <Detail label="PUC application ref" value={a.pucApplicationRef} /> : null}
            {a.pucMeterSwapDate ? <Detail label="PUC meter swap date" value={formatDate(a.pucMeterSwapDate)} /> : null}
          </div>
          {a.rejectionReason ? <ErrorNote message={`Rejected: ${a.rejectionReason}`} /> : null}

          {a.state === 'SUBMITTED' ? (
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="mb-2 text-sm font-semibold text-slate-700">Installer co-sign</div>
              <p className="mb-2 text-xs text-slate-500">
                The signed-in officer's own NIN must match M29's registrant for licence {a.installerLicenceNo} (live GET /api/v1/m29/verify/professional/:licenceNo check) — this fails
                honestly if it doesn't match a real M29 professional.
              </p>
              <FileHashField label="Single-line diagram" onChange={setSingleLine} required />
              <button
                className="btn-primary mt-2"
                disabled={busy || !singleLine}
                onClick={() =>
                  act(() =>
                    api.post(`/api/v1/m35/pv-applications/${id}/installer-cosign`, {
                      singleLineDocRef: singleLine!.docRef,
                      singleLineSha256: singleLine!.sha256,
                      singleLineFileName: singleLine!.fileName,
                    }),
                  )
                }
              >
                Co-sign as installer
              </button>
            </div>
          ) : null}

          {isOfficer && a.state === 'INSTALLER_COSIGNED' ? (
            <button className="btn-primary" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m35/pv-applications/${id}/claim-review`))}>
              Claim for review
            </button>
          ) : null}

          {isOfficer && a.state === 'UNDER_REVIEW' ? (
            <div className="flex flex-wrap gap-2">
              <button className="btn-success" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m35/pv-applications/${id}/approve`))}>
                Approve (issue DocVault certificate)
              </button>
              <div className="flex gap-2">
                <input className="field-input" placeholder="Rejection reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
                <button className="btn-danger" disabled={busy || rejectReason.trim().length < 3} onClick={() => act(() => api.post(`/api/v1/m35/pv-applications/${id}/reject`, { reason: rejectReason }))}>
                  Reject
                </button>
              </div>
            </div>
          ) : null}

          {isOfficer && a.state === 'APPROVED' ? (
            <div className="flex gap-2 rounded-lg border border-slate-200 p-3">
              <Field label="PUC application ref" className="flex-1">
                <input className="field-input" value={pucRef} onChange={(e) => setPucRef(e.target.value)} />
              </Field>
              <Field label="Meter swap date (optional)" className="flex-1">
                <input className="field-input" type="date" value={meterSwapDate} onChange={(e) => setMeterSwapDate(e.target.value)} />
              </Field>
              <button
                className="btn-primary self-end"
                disabled={busy || !pucRef.trim()}
                onClick={() => act(() => api.post(`/api/v1/m35/pv-applications/${id}/puc-handoff`, { pucApplicationRef: pucRef, pucMeterSwapDate: meterSwapDate || undefined }))}
              >
                Record PUC handoff
              </button>
            </div>
          ) : null}

          {isOfficer && a.state === 'PUC_HANDED_OFF' ? (
            <button className="btn-success" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m35/pv-applications/${id}/commission`))}>
              Mark commissioned
            </button>
          ) : null}

          {['SUBMITTED', 'INSTALLER_COSIGNED', 'UNDER_REVIEW', 'APPROVED'].includes(a.state) ? (
            <div className="flex gap-2">
              <input className="field-input" placeholder="Withdraw reason (optional)" value={withdrawReason} onChange={(e) => setWithdrawReason(e.target.value)} />
              <button className="btn-danger" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m35/pv-applications/${id}/withdraw`, { reason: withdrawReason || undefined }))}>
                Withdraw
              </button>
            </div>
          ) : null}

          <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">
            Submitted {formatDateTime(a.createdAt)} · last updated {formatDateTime(a.updatedAt)}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}

function CreateModal({ isOfficer, onClose, onDone }: { isOfficer: boolean; onClose: () => void; onDone: () => void }) {
  const [applicantType, setApplicantType] = useState<ApplicantType>('PERSON');
  const [applicantId, setApplicantId] = useState('');
  const [parcelNo, setParcelNo] = useState('');
  const [premisesAddress, setPremisesAddress] = useState('');
  const [district, setDistrict] = useState('');
  const [systemKw, setSystemKw] = useState('');
  const [installerName, setInstallerName] = useState('');
  const [installerLicenceNo, setInstallerLicenceNo] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m35/pv-applications', {
        ...(isOfficer && applicantId ? { applicantId, applicantType } : {}),
        parcelNo,
        premisesAddress,
        district,
        systemKw: Number.parseFloat(systemKw),
        installerName,
        installerLicenceNo,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to submit application');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New PV solar application" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        {isOfficer ? (
          <div className="grid grid-cols-2 gap-3 rounded-lg border border-slate-200 p-3">
            <Field label="Applicant type (counter intake — optional)">
              <select className="field-input" value={applicantType} onChange={(e) => setApplicantType(e.target.value as ApplicantType)}>
                <option value="PERSON">Person</option>
                <option value="ENTITY">Entity</option>
              </select>
            </Field>
            <Field label="Applicant id (NIN / business id — optional)">
              <input className="field-input" value={applicantId} onChange={(e) => setApplicantId(e.target.value)} placeholder="leave blank to submit as yourself" />
            </Field>
          </div>
        ) : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Parcel number">
            <input className="field-input" value={parcelNo} onChange={(e) => setParcelNo(e.target.value)} />
          </Field>
          <Field label="District">
            <input className="field-input" value={district} onChange={(e) => setDistrict(e.target.value)} />
          </Field>
        </div>
        <Field label="Premises address">
          <input className="field-input" value={premisesAddress} onChange={(e) => setPremisesAddress(e.target.value)} />
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="System size (kW)">
            <input className="field-input" type="number" step="0.01" value={systemKw} onChange={(e) => setSystemKw(e.target.value)} />
          </Field>
          <Field label="Installer name">
            <input className="field-input" value={installerName} onChange={(e) => setInstallerName(e.target.value)} />
          </Field>
          <Field label="Installer M29 licence no">
            <input className="field-input" value={installerLicenceNo} onChange={(e) => setInstallerLicenceNo(e.target.value)} />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={busy || !parcelNo || !premisesAddress || !district || !systemKw || !installerName || !installerLicenceNo}
            onClick={submit}
          >
            {busy ? 'Submitting…' : 'Submit application'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
